import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronRight, FileSignature } from "lucide-react";
import { api } from "../api/client";
import BottomNav from "../components/BottomNav";
import DocumentPreviewModal from "../components/DocumentPreviewModal";
import SignatureModal from "../components/SignatureModal";
import { PREVIEWABLE_TYPES, openDocument } from "../utils/documentFiles";

interface Doc { id: number; title: string; file_name: string; file_type: string; category_id: number; expires_at: string | null; requires_signature: boolean; requires_initials: boolean; signed_at: string | null; signature_mode: string | null; acknowledged_at: string | null; created_at: string; }

export default function DocumentsPage() {
  const { t } = useTranslation();
  const [docs, setDocs] = useState<Doc[]>([]);
  const [signingDoc, setSigningDoc] = useState<Doc | null>(null);
  const [previewDoc, setPreviewDoc] = useState<Doc | null>(null);
  const [jd, setJd] = useState<{ status: string; version: number } | null>(null);

  const load = useCallback(async () => {
    try { setDocs(await api.get<Doc[]>("/api/documents/my")); } catch {}
    try {
      const p = await api.get<{ current: { status: string; version: number } | null }>("/api/job-descriptions/me");
      setJd(p.current);
    } catch {}
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="flex min-h-dvh flex-col bg-white safe-x">
      <header className="safe-top px-5 pt-4 pb-3">
        <h1 className="text-2xl font-semibold text-gray-900">{t("nav.documents")}</h1>
      </header>
      <main className="flex-1 overflow-y-auto px-5 pb-24 space-y-2">
        {jd && (
          <Link
            to="/job-description"
            className={`flex items-center gap-3 rounded-xl border p-4 shadow-sm ${jd.status === "signed" ? "border-gray-200 bg-white" : "border-pme-blue/40 bg-pme-sky/40"}`}
          >
            <FileSignature size={20} className="shrink-0 text-pme-blue" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-gray-900">{t("jd.docsCard.title", "My job description")}</span>
              <span className="block text-xs text-gray-600">
                {jd.status === "signed"
                  ? t("jd.docsCard.signed", "Version {{n}}, signed", { n: jd.version })
                  : jd.status === "changes_requested"
                    ? t("jd.docsCard.asked", "You asked for a change")
                    : t("jd.docsCard.toSign", "Version {{n}}, please read and sign", { n: jd.version })}
              </span>
            </span>
            <ChevronRight size={18} className="shrink-0 text-gray-400" aria-hidden />
          </Link>
        )}
        {docs.length === 0 && !jd && <p className="text-sm text-gray-400">{t("common.noData")}</p>}
        {docs.map((d) => (
          <div key={d.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">{d.title}</p>
            <p className="text-xs text-gray-500">{d.file_name} · {d.file_type.toUpperCase()}</p>
            {d.expires_at && <p className="text-xs text-amber-600">Expires: {d.expires_at}</p>}
            {d.requires_signature && !d.signed_at && <span className="mt-1 inline-block rounded bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">Signature Required</span>}
            {d.signed_at && <span className="mt-1 inline-block rounded bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700">Signed</span>}
            <div className="mt-2 flex flex-wrap gap-2">
              {PREVIEWABLE_TYPES.has(d.file_type.toLowerCase()) && (
                <button type="button" onClick={() => setPreviewDoc(d)} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">{t("documents.view", "View")}</button>
              )}
              <button type="button" onClick={() => void openDocument(d.id, "attachment")} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">Download</button>
              {d.requires_signature && !d.signed_at && (
                <button type="button" onClick={() => setSigningDoc(d)} className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600">Sign</button>
              )}
            </div>
          </div>
        ))}
      </main>

      {signingDoc && (
        <SignatureModal
          doc={signingDoc}
          onClose={(signed) => {
            setSigningDoc(null);
            if (signed) void load();
          }}
        />
      )}

      {previewDoc && (
        <DocumentPreviewModal
          documentId={previewDoc.id}
          title={previewDoc.title}
          fileType={previewDoc.file_type}
          onClose={() => setPreviewDoc(null)}
        />
      )}

      <BottomNav />
    </div>
  );
}
